import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { previewKindOfContentType, type PreviewKind } from '../../core/materials';
import {
  PAGE_GAP, anchorPlacement, anchorScrollTop, clampAnchor, documentHeight, isHighlightAnchor,
  pageAtOffset, pageOffsets, planDocument, renderWindow,
  type HighlightAnchor, type PageSize,
} from '../../core/page-geometry';
import {
  domSaver, fetchMaterialPreview,
  type GetToken, type MaterialSummary, type ObjectUrls,
} from '../api';
import { anchorFromDocumentSelection } from '../highlights';
import { pdfJsEngine, type PdfDocument, type PdfEngine } from '../pdf';
import { passageDrawer, type DrawPassage } from '../pdf-snapshot';
import PdfPageBox, { type PageHighlight } from './PdfPageBox';

/**
 * The material itself, on screen, beside what is being said about it (U11).
 *
 * Four rules, and each of them is the reason one of the others is safe:
 *
 * 1. **Three types render and everything else downloads.** `previewKindOfContentType` is the
 *    allowlist, shared with the Worker, and it answers for PDF, PNG and JPEG only. A .docx
 *    or a .txt is not an error and is not a dead grey rectangle — it says what it is and
 *    offers the download, which is what a teacher came for anyway.
 * 2. **The bytes arrive through `fetch` with the bearer token.** An `<img src>` sends no
 *    `Authorization` header, and `?token=` would put a Clerk JWT into Worker logs, referrers
 *    and PostHog's `$current_url` — the leak `api.ts` is written around. No URL this
 *    component builds carries a credential, and pdf.js is handed bytes rather than a URL for
 *    the same reason: given a URL it would make its own request, unauthenticated.
 * 3. **Every handle is released.** An image's object URL is revoked and a PDF document is
 *    `destroy()`ed — on unmount, on a change of material, and on a fetch that resolved after
 *    either. A blob URL that outlives its viewer is a live handle to a colleague's material;
 *    a pdf.js document that outlives its viewer is a Web Worker and a parsed copy of the
 *    file. The cleanups run in both orders, which is why the created URL is revoked inside
 *    the async body too when the effect has already been torn down.
 * 4. **A PDF is rendered by pdf.js onto a `<canvas>`, and is never embedded.** This is the
 *    whole of why the previous iframe is gone. A sandboxed `<iframe>` with no `allow-scripts`
 *    was the right posture and did not render — Chrome's PDF viewer is itself a scripted
 *    document, so it drew a broken-file icon (measured in a real browser across four sandbox
 *    values). pdf.js parses the file and paints it; the document's own JavaScript is never
 *    executed by anything. **No PDF ever becomes an object URL here**, so nothing about a
 *    PDF is ever navigable, framable or plugin-loadable on this origin.
 *
 * ## The page box
 *
 * A painted page used to be "a canvas at whatever width the panel happened to be", and it was
 * unreadable. It is now three layers stacked inside one **page box**, whose size is decided in
 * one place — `planPageRender`, in `core/`, which is arithmetic and is tested as arithmetic:
 *
 * - the **canvas**, whose backing store is the box times the device pixel ratio, capped at 2.
 *   Without that multiplier a retina screen shows a page upscaled from half resolution, which
 *   is exactly what "the PDF is tiny" turns into once the page is big enough to read;
 * - the **text layer**, pdf.js's own, positioned as percentages of the page and scaled by one
 *   CSS variable, so it is pinned to the canvas at every zoom rather than re-aligned at each;
 * - the **highlight layer**, drawn from anchors in `0..1` page coordinates.
 *
 * Because all three are measured against the same box, alignment is not something this file
 * maintains — it is something the shared number makes true.
 *
 * ## One document, scrolled — and a window of three
 *
 * The pager is gone. A teacher reading a unit plan scrolls it; they do not press Next.
 *
 * The pager existed for a real reason, and that reason has not gone away: fit-width at a
 * device pixel ratio of 2 is tens of megabytes of backing store per page, so a forty-page
 * document held live is a tab the system kills. What replaces the pager is **bounded
 * windowing**, and it rests on one fact — *a page's size does not require painting it*.
 * `getViewport` answers from the page's own dictionary, so:
 *
 * - **every page gets its true box up front**, which is why the scroll height is right on the
 *   first frame and the scrollbar does not jump as pages paint;
 * - **only the pages around the one being read hold a canvas** — the current page and one
 *   either side. Three live canvases at any scroll position, whatever the document's length,
 *   and a page leaving the window has its backing store set to zero rather than merely being
 *   hidden (`PdfPageBox`, which is where both halves live).
 *
 * The page indicator and the window both come from `pageAtOffset`, which is arithmetic on the
 * scroll position, so "Page 7 of 40" and "which three pages are painted" cannot disagree.
 *
 * ## A highlight lasts exactly as long as something is said about it
 *
 * Selecting text draws a highlight and offers to comment on it. Post the comment and the
 * anchor is stored with it; navigate away without commenting and it was never anything. That
 * is the whole rule, and it is why there is no highlight table, no orphan to sweep up, and
 * nothing to clean when a teacher changes their mind mid-drag.
 *
 * Saved anchors come back down from `MaterialPage` with their comment's id, and the two
 * directions of the study app's grading panel work here too: clicking a highlight focuses its
 * comment, and clicking a comment scrolls the document to its highlight.
 *
 * `collab-material-viewer` is a replay block selector. The panel renders the material, and a
 * recording of a teacher's unit plan is the thing this product would least survive leaking.
 */

/** A saved anchor and the comment it belongs to. */
export interface AnchoredComment {
  readonly commentId: string;
  /** Straight off the wire, so the viewer is the one that decides whether it can be drawn. */
  readonly anchor: unknown;
}

export interface MaterialViewerProps {
  material: MaterialSummary;
  getToken: GetToken;
  /** Injected in tests, so object URLs assert without a real `URL.createObjectURL`. */
  urls?: ObjectUrls;
  /** Injected in tests, so the PDF path asserts without a real parser inside jsdom. */
  pdf?: PdfEngine;
  onDownload: (material: MaterialSummary) => void;
  /** A 404 on an open session means the membership went away underneath it (R5, AE4). */
  onAccessLost: () => void;
  /** Emitted once per material actually rendered. Browser-side and nowhere else (KTD15). */
  onPreviewed: (kind: PreviewKind) => void;
  /** The saved anchors, with the comments they hang from. */
  anchors?: readonly AnchoredComment[];
  /** Which comment is in focus, so its highlight can say so. */
  focusedCommentId?: string | null;
  /** A teacher selected a passage. The parent decides what to offer them. */
  onSelect?: (anchor: HighlightAnchor) => void;
  /** A teacher clicked a saved highlight. */
  onAnchorActivated?: (commentId: string) => void;
  /** How many pages the document turned out to have. Null until it is open. */
  onPageCount?: (count: number | null) => void;
  /** A way to paint a passage from the open document, or null while there is none. */
  onDrawPassage?: (draw: DrawPassage | null) => void;
  /** Bumped by the parent to drop the selection in hand — it was posted, or abandoned. */
  clearSelection?: number;
  /** Bumped by the parent to scroll to a comment's passage. */
  scrollTo?: { commentId: string; nonce: number } | null;
}

type ViewerState = 'loading' | 'ready' | 'failed' | 'unreadable' | 'not_previewable';

/**
 * What a page is painted at when the stage has not been laid out yet — jsdom, and the first
 * frame of a panel that is still measuring. Wide enough that a fallback render is legible
 * rather than a thumbnail.
 */
const FALLBACK_WIDTH = 900;

/**
 * The zoom ladder. `1` is fit-to-width and is where a page opens.
 *
 * Fit-width is called 100% rather than the page's true scale because "100%" has to mean *the
 * size I opened it at* for the plus and minus buttons to read as bigger and smaller. A teacher
 * reading a worksheet in a column has no use for a number about points per inch.
 */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;
const FIT_WIDTH = 1;

/**
 * How long a drag settles before the pages are re-planned, in milliseconds.
 *
 * A `ResizeObserver` fires on every frame of a window drag. Re-planning on each queues dozens
 * of render tasks, every one of which is cancelled by the next — visibly, as a page that
 * flickers while the window moves and is blank when it stops. One re-plan, when the dragging
 * has stopped.
 */
const RESIZE_SETTLE_MS = 120;

/** A width change smaller than this is a reflow, not a resize. */
const RESIZE_EPSILON = 4;

export default function MaterialViewer({
  material, getToken, urls = domSaver, pdf = pdfJsEngine,
  onDownload, onAccessLost, onPreviewed,
  anchors = [], focusedCommentId = null,
  onSelect, onAnchorActivated, onPageCount, onDrawPassage,
  clearSelection = 0, scrollTo = null,
}: MaterialViewerProps) {
  const { id: materialId, contentType } = material;
  const kind = previewKindOfContentType(contentType);
  const [state, setState] = useState<ViewerState>(kind === null ? 'not_previewable' : 'loading');
  /** An image's object URL. A PDF never gets one — see rule 4. */
  const [source, setSource] = useState<string | null>(null);
  /** A PDF's bytes, exactly as fetched. Handed to pdf.js and to nothing else. */
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [doc, setDoc] = useState<PdfDocument | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [zoom, setZoom] = useState<number>(FIT_WIDTH);
  /** Every page at scale 1, read before anything is painted. The boxes come from these. */
  const [sizes, setSizes] = useState<readonly PageSize[] | null>(null);
  /** Where the reading eye is, from the scroll position and nothing else. */
  const [currentPage, setCurrentPage] = useState(1);
  /** The selection in hand. It is drawn, and it is stored only if a comment is written on it. */
  const [pending, setPending] = useState<HighlightAnchor | null>(null);
  /** Pages that reported no text to select. A scan, usually. */
  const [textless, setTextless] = useState<ReadonlySet<number>>(() => new Set());

  const stageRef = useRef<HTMLDivElement | null>(null);
  /** The CSS width the pages were last planned at. Compared against, never rendered. */
  const plannedAt = useRef(0);
  const [stageWidth, setStageWidth] = useState(0);

  /**
   * The callbacks, held rather than depended on.
   *
   * A parent that passes an inline arrow — which `MaterialPage` does — hands this component a
   * new function every render. In the dependency list that is a re-fetch per render, which
   * is a revoke and a re-render per render: a loop that ends in the group's materials being
   * downloaded forever. The effects depend on the material and on nothing that changes with
   * a render.
   */
  const handlers = useRef({
    onAccessLost, onPreviewed, onSelect, onAnchorActivated, onPageCount, onDrawPassage, materialId,
  });
  handlers.current = {
    onAccessLost, onPreviewed, onSelect, onAnchorActivated, onPageCount, onDrawPassage, materialId,
  };

  // Once per material rendered, and not twice under StrictMode's double invocation.
  const announced = useRef<string | null>(null);

  // --- the bytes ---------------------------------------------------------------------------

  useEffect(() => {
    if (kind === null) {
      setState('not_previewable');
      setSource(null);
      setBytes(null);
      return;
    }

    let live = true;
    let created: string | null = null;
    setState('loading');
    setSource(null);
    setBytes(null);

    void (async () => {
      const result = await fetchMaterialPreview(getToken, { id: materialId, contentType });

      if (!result.ok) {
        if (!live) return;
        if (result.status === 404) { handlers.current.onAccessLost(); return; }
        setState('failed');
        return;
      }

      if (result.data.kind === 'pdf') {
        // No object URL. pdf.js takes the bytes, and a PDF that is never a `blob:` URL is
        // never something this origin can navigate to, frame or hand to a plugin.
        setBytes(result.data.bytes);
        return;
      }

      const url = urls.createObjectURL(result.data.blob);
      // The effect was torn down while the bytes were in flight. Nothing will ever render
      // this URL, and the cleanup below has already run, so it is revoked here or never.
      if (!live) { urls.revokeObjectURL(url); return; }

      created = url;
      setSource(url);
      setState('ready');
      if (announced.current !== materialId) {
        announced.current = materialId;
        handlers.current.onPreviewed(result.data.kind);
      }
    })();

    return () => {
      live = false;
      if (created) urls.revokeObjectURL(created);
    };
    // `attempt` is in the list so "Try again" re-runs the whole effect, revoke included.
  }, [materialId, contentType, kind, getToken, urls, attempt]);

  // --- the document ------------------------------------------------------------------------

  useEffect(() => {
    setDoc(null);
    setSizes(null);
    if (bytes === null) return;

    let live = true;
    let loaded: PdfDocument | null = null;

    void (async () => {
      let opened: PdfDocument;
      try {
        opened = await pdf.load(bytes);
      } catch {
        // A truncated upload, a file that is a PDF only by its header, an encrypted one.
        // Stated as a fact about the file, with the download still offered — not a blank
        // frame, and not an unhandled rejection in the console.
        if (live) setState('unreadable');
        return;
      }

      if (!live) {
        // Parsed after the viewer was already gone. The cleanup below has run, so this
        // document's worker is released here or never.
        void opened.destroy();
        return;
      }

      loaded = opened;
      setDoc(opened);
      setCurrentPage(1);
      setZoom(FIT_WIDTH);
      setState('ready');
      // `materialId` is read off the ref rather than closed over, and is deliberately not a
      // dependency: it changes on the render *before* the new material's bytes arrive, so a
      // dependency on it tears down and reloads the document that is still on screen — two
      // destroys and three parses for one change of material.
      const current = handlers.current.materialId;
      if (announced.current !== current) {
        announced.current = current;
        handlers.current.onPreviewed('pdf');
      }
    })();

    return () => {
      live = false;
      if (loaded) void loaded.destroy();
    };
  }, [bytes, pdf]);

  // --- every page's size, before any of them is painted -------------------------------------

  /**
   * The one thing that makes a continuous document possible without holding it all in memory.
   *
   * `getViewport` reads the page's declared box; it does not rasterise anything. So this walks
   * the whole document once, cheaply, and everything downstream — the scroll height, the
   * indicator, which three pages are painted — is arithmetic on the result.
   */
  useEffect(() => {
    if (doc === null) return;
    let live = true;

    void (async () => {
      const measured: PageSize[] = [];
      for (let number = 1; number <= doc.numPages; number += 1) {
        let page;
        try {
          page = await doc.getPage(number);
        } catch {
          if (live) setState('unreadable');
          return;
        }
        if (!live) return;
        const viewport = page.getViewport({ scale: 1 });
        measured.push({ width: viewport.width, height: viewport.height });
      }
      if (!live) return;
      setSizes(measured);
    })();

    return () => { live = false; };
  }, [doc]);

  useEffect(() => {
    handlers.current.onPageCount?.(sizes === null ? null : sizes.length);
  }, [sizes]);

  // The thread paints its quoted passages from this document, so it is handed over while open
  // and taken back before `destroy`.
  useEffect(() => {
    handlers.current.onDrawPassage?.(doc === null ? null : passageDrawer(doc));
    return () => { handlers.current.onDrawPassage?.(null); };
  }, [doc]);

  // --- the stage's width -------------------------------------------------------------------

  /**
   * Pages are planned at the stage's width, so the stage changing width re-plans them — once.
   *
   * Without this, a document opened on a narrow panel and then widened — a rotated phone, a
   * dragged window, the comments panel being hidden — is a small canvas stretched by CSS,
   * which is a blurry worksheet. The comparison is against the width the pages were *planned*
   * at rather than against the last width observed, so the paint that follows a resize does
   * not itself count as a resize; and the debounce is what turns a drag from dozens of
   * cancelled render tasks into one.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null || typeof ResizeObserver === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[entries.length - 1]?.contentRect.width ?? 0);
      if (width <= 0 || Math.abs(plannedAt.current - width) <= RESIZE_EPSILON) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        plannedAt.current = width;
        setStageWidth(width);
      }, RESIZE_SETTLE_MS);
    });
    observer.observe(stage);

    return () => {
      if (timer !== null) clearTimeout(timer);
      observer.disconnect();
    };
  }, [kind]);

  // --- the boxes ----------------------------------------------------------------------------

  /**
   * Every page's box, re-derived whenever the width or the zoom changes.
   *
   * Pure arithmetic over sizes that were read once, so a zoom re-plans forty boxes without
   * touching pdf.js at all — and the three live pages repaint because their `plan` changed.
   */
  const plans = useMemo(() => (
    sizes === null ? [] : planDocument(sizes, {
      containerWidth: stageWidth > 0 ? stageWidth : FALLBACK_WIDTH,
      zoom,
      devicePixelRatio: typeof window === 'undefined' ? 1 : (window.devicePixelRatio || 1),
    })
  ), [sizes, stageWidth, zoom]);

  const pageCount = plans.length;
  const window_ = renderWindow(currentPage, pageCount);

  /**
   * A zoom keeps the page a teacher was on.
   *
   * Every box changes height, so the scroll position a teacher had means a different place in
   * the document afterwards — zoom in on page 12 of a unit plan and land on page 7 is the
   * classic version of this bug. The page number is what is preserved, because that is what a
   * teacher was looking at.
   */
  const keepPage = useRef<number | null>(null);
  useEffect(() => {
    const stage = stageRef.current;
    const wanted = keepPage.current;
    keepPage.current = null;
    if (stage === null || wanted === null || plans.length === 0) return;
    const top = pageOffsets(plans)[Math.min(wanted, plans.length) - 1];
    if (top !== undefined) stage.scrollTop = top;
  }, [plans]);

  // --- where the teacher is, and which pages are painted -------------------------------------

  const onScroll = useCallback(() => {
    const stage = stageRef.current;
    if (stage === null || plans.length === 0) return;
    const next = pageAtOffset(plans, stage.scrollTop, stage.clientHeight);
    setCurrentPage((current) => (current === next ? current : next));
  }, [plans]);

  // --- selection becomes a highlight in hand -------------------------------------------------

  /**
   * Whatever is selected inside a page's text layer, pinned to that page.
   *
   * It runs when a selection *finishes* — `pointerup`, `keyup` — rather than on
   * `selectionchange`, which fires on every pixel of a drag: an anchor per pixel is a
   * flickering overlay and a hundred posts for one sentence. The listeners are on the document
   * rather than on the page box because a drag very often ends with the pointer outside the
   * page, and a highlight that depends on where the mouse was let go is a highlight that works
   * half the time.
   *
   * Which page it belongs to comes from the selection itself, not from a page number this
   * component is holding — with forty boxes on one scroll there is no such number.
   */
  const capture = useCallback(() => {
    const stage = stageRef.current;
    if (stage === null) return;

    const anchor = anchorFromDocumentSelection({
      selection: typeof window === 'undefined' ? null : window.getSelection(),
      stage,
    });
    if (anchor === null) return;

    // Clamped here, so what is drawn is exactly what would be stored — and so a teacher who
    // selects six paragraphs gets a comment rather than a refusal from the Worker's ceilings.
    const clamped = clampAnchor(anchor);
    setPending(clamped);
    handlers.current.onSelect?.(clamped);
  }, []);

  useEffect(() => {
    if (kind !== 'pdf' || typeof document === 'undefined') return;
    const finished = () => { capture(); };
    document.addEventListener('pointerup', finished);
    document.addEventListener('keyup', finished);
    return () => {
      document.removeEventListener('pointerup', finished);
      document.removeEventListener('keyup', finished);
    };
  }, [capture, kind]);

  // The parent posted it, or dropped it. Either way the selection in hand is spent.
  useEffect(() => { setPending(null); }, [clearSelection, materialId]);

  // --- scrolling to a comment's passage -------------------------------------------------------

  /**
   * The other half of the study app's grading panel: a comment clicked, the document moved.
   *
   * The nonce is what makes it a *command* rather than a state. Without it, any re-render that
   * changed the anchors would scroll the document out from under a teacher who had since
   * scrolled somewhere else.
   */
  const scrolled = useRef(0);
  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null || scrollTo === null || scrollTo.nonce === scrolled.current) return;
    if (plans.length === 0) return;

    const found = anchors.find((entry) => entry.commentId === scrollTo.commentId);
    if (found === undefined || !isHighlightAnchor(found.anchor)) return;
    const top = anchorScrollTop(plans, found.anchor, stage.clientHeight);
    if (top === null) return;

    scrolled.current = scrollTo.nonce;
    if (typeof stage.scrollTo === 'function') stage.scrollTo({ top, behavior: 'smooth' });
    else stage.scrollTop = top;
    // jsdom and a smooth scroll both settle later than this line; the indicator and the render
    // window must not wait for a scroll event that may be several frames away.
    setCurrentPage(found.anchor.page);
  }, [scrollTo, anchors, plans]);

  // --- what to draw on each page ---------------------------------------------------------------

  /** Saved anchors this document can actually place, grouped by page. */
  const drawable = useMemo(() => {
    const byPage = new Map<number, PageHighlight[]>();
    const add = (page: number, highlight: PageHighlight) => {
      const list = byPage.get(page);
      if (list) list.push(highlight);
      else byPage.set(page, [highlight]);
    };

    for (const entry of anchors) {
      // An anchor past the end of this document, or one whose rects no longer describe a page,
      // is not drawn *anywhere* — the thread renders it as a detached quote instead. Drawing it
      // on page 1 would be pointing confidently at the wrong place, which is the one failure
      // this is not allowed to have.
      if (anchorPlacement(entry.anchor, pageCount === 0 ? null : pageCount) !== 'placed') continue;
      const anchor = entry.anchor as HighlightAnchor;
      add(anchor.page, { commentId: entry.commentId, anchor });
    }
    if (pending !== null) add(pending.page, { commentId: null, anchor: pending });

    return byPage;
  }, [anchors, pending, pageCount]);

  const activate = useCallback((commentId: string) => {
    handlers.current.onAnchorActivated?.(commentId);
  }, []);

  const noteText = useCallback((page: number, hasText: boolean) => {
    setTextless((current) => {
      if (hasText === !current.has(page)) return current;
      const next = new Set(current);
      if (hasText) next.delete(page); else next.add(page);
      return next;
    });
  }, []);

  const noteUnreadable = useCallback(() => { setState('unreadable'); }, []);

  const zoomIndex = ZOOM_STEPS.indexOf(zoom as (typeof ZOOM_STEPS)[number]);
  const zoomPercent = Math.round(zoom * 100);

  const step = (direction: 1 | -1) => {
    keepPage.current = currentPage;
    setZoom((current) => {
      const at = ZOOM_STEPS.indexOf(current as (typeof ZOOM_STEPS)[number]);
      const from = at === -1 ? ZOOM_STEPS.indexOf(FIT_WIDTH) : at;
      return ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, from + direction))] ?? current;
    });
  };

  return (
    <section className="panel viewer collab-material-viewer" aria-label="Material">
      {/*
        * No header of its own. The page above this one carries the toolbar — the material's
        * name and filename on the left, Download and the way back on the right — in the shape
        * the study app's workshops viewer uses. A second copy of the title one panel down read
        * as two different things, and put an `h3` where the page's `h2` already is.
        */}
      {material.note && <p className="note viewer-note">{material.note}</p>}

      {state === 'loading' && (
        <div className="viewer-stage viewer-stage-empty">
          <p className="hint" role="status">Opening this material…</p>
        </div>
      )}

      {state === 'failed' && (
        <div className="viewer-stage viewer-stage-empty" role="alert">
          <p className="error">We could not open this material.</p>
          <button type="button" className="secondary" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      )}

      {state === 'unreadable' && (
        // The bytes arrived and are not a PDF this reader can parse — damaged, truncated or
        // password-protected. "Try again" would fail identically, so it is not offered; the
        // download is, because the teacher's own PDF reader may well manage it.
        <div className="viewer-stage viewer-stage-empty" role="alert">
          <p className="error">
            We could not read this PDF. It may be damaged or password-protected.
          </p>
          <button type="button" className="primary" onClick={() => onDownload(material)}>
            Download it instead
          </button>
        </div>
      )}

      {state === 'not_previewable' && (
        // Not an error and not a blank frame. It says which kinds have a viewer, so the
        // absence reads as a fact about the file rather than as something that broke.
        <div className="viewer-stage viewer-stage-empty">
          <p className="hint">
            This one opens in its own app. PDFs and images show here; Word, PowerPoint and
            text files download.
          </p>
          <button type="button" className="primary" onClick={() => onDownload(material)}>
            Download to read it
          </button>
        </div>
      )}

      {state === 'ready' && source !== null && kind === 'image' && (
        <div className="viewer-stage">
          <img className="viewer-image" src={source} alt={material.title} />
        </div>
      )}

      {kind === 'pdf' && (
        // Mounted for every state the PDF path can be in, not only `ready`: the render effects
        // need the stage and the page boxes to exist before they can fill them, and an element
        // that appears only once rendering has finished can never be what rendering finished
        // into.
        <div className="viewer-pdf" hidden={state !== 'ready'}>
          <div className="viewer-controls">
            <div className="viewer-zoom" role="group" aria-label="Zoom">
              <button
                type="button"
                className="secondary"
                aria-label="Zoom out"
                disabled={zoomIndex === 0}
                onClick={() => step(-1)}
              >
                −
              </button>
              <p className="meta viewer-zoom-readout" role="status">{zoomPercent}%</p>
              <button
                type="button"
                className="secondary"
                aria-label="Zoom in"
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
                onClick={() => step(1)}
              >
                +
              </button>
              <button
                type="button"
                className="secondary"
                aria-pressed={zoom === FIT_WIDTH}
                onClick={() => { keepPage.current = currentPage; setZoom(FIT_WIDTH); }}
              >
                Fit width
              </button>
            </div>

            {pageCount > 0 && (
              // Where you are, not a control. The scroll position decides it, which is why it
              // is a `status` rather than a pager: there is nothing here to press.
              <p className="meta viewer-place" role="status">
                Page {currentPage} of {pageCount}
              </p>
            )}
          </div>

          {/*
            * Above the document, not below it. A letter-size page at fit-width is over a
            * thousand CSS pixels tall, so a notice under it is a notice nobody reads — and
            * "this is what you selected" has to be read while the selection is still in hand.
            */}
          <div className="viewer-footnotes">
            {textless.has(currentPage) && (
              <p className="hint" role="status">
                This page has no text to select — it may be a scan. The picture of it is all
                there is.
              </p>
            )}
            {pending !== null && (
              <p className="hint viewer-selected" role="status">
                Passage selected. Write a comment on it and the highlight stays; leave it and
                nothing is kept.
                {' '}
                <button type="button" className="link-button" onClick={() => setPending(null)}>
                  Clear it
                </button>
              </p>
            )}
          </div>

          <div
            ref={stageRef}
            className="viewer-stage viewer-stage-pdf"
            onScroll={onScroll}
          >
            <div
              className="viewer-doc"
              // The gap is the one `pageOffsets` counts. Set from the constant rather than
              // from the stylesheet so the arithmetic and the layout cannot disagree — and a
              // disagreement here is a page indicator that drifts by a page over forty of them.
              style={{ gap: `${PAGE_GAP}px`, minHeight: `${documentHeight(plans)}px` }}
            >
              {plans.map((plan, index) => (
                <PdfPageBox
                  key={index}
                  doc={doc as PdfDocument}
                  pageNumber={index + 1}
                  pageCount={pageCount}
                  plan={plan}
                  live={index + 1 >= window_.first && index + 1 <= window_.last}
                  title={material.title}
                  highlights={drawable.get(index + 1) ?? []}
                  focusedCommentId={focusedCommentId}
                  onActivate={activate}
                  onText={noteText}
                  onUnreadable={noteUnreadable}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
